<app-section-grid *ngIf="!embeddedMode" [subSection]="'Edit Social Platform'"></app-section-grid>

<div class="relative w-full mx-auto p-10 bg-white rounded-3xl mb-5"
     [class.border]="hasBorder"
     [class.border-gray-200]="hasBorder"
     style="max-width: 760px;">

  <form [formGroup]="platformForm" (ngSubmit)="onSubmit()">

    <!-- Header -->
    <div class="text-center mb-4">
      <h1 class="text-3xl font-bold text-gray-800 tracking-tight">
        Edit Social Platform Details
      </h1>
      <p class="mt-2 text-gray-500">
        Update the social platform details below.
      </p>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-6">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-3 text-gray-600">Loading platform data...</p>
    </div>

    <!-- Messages aligned with fields -->
    <div *ngIf="!isLoading" class="w-full max-w-3xl mx-auto">
      <!-- Error Message -->
      <div *ngIf="errorMessage"
           class="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
        <div class="flex items-center">
          <svg class="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd" />
          </svg>
          <span class="text-red-700 text-sm">{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Success Message -->
      <div *ngIf="successMessage"
           class="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
        <div class="flex items-center">
          <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd" />
          </svg>
          <span class="text-green-700 text-sm">{{ successMessage }}</span>
        </div>
      </div>
    </div>

    <!-- Fields -->
    <div *ngIf="!isLoading" class="w-full max-w-3xl mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

        <!-- Platform -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            🏷️ Platform <small class="text-red-300">(required)</small>
          </label>
          <select
            formControlName="socialPlatform"
            (change)="onPlatformChange()"
            class="cursor-pointer w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
            <option value="" disabled>Select platform</option>
            <option value="Facebook">Facebook</option>
            <option value="Instagram">Instagram</option>
            <!-- <option value="TikTok">TikTok</option> -->
          </select>

          <div *ngIf="getControl('socialPlatform')?.touched && getControl('socialPlatform')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('socialPlatform')?.hasError('required')">
              Platform is required.
            </span>
          </div>
        </div>

        <!-- Sites -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            🗂️ Sites
          </label>
          <select
            formControlName="siteID"
            class="cursor-pointer w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors
                   disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500">
            <option value="" disabled>Select social page/site</option>

            <option
              *ngFor="let site of siteOptions"
              [value]="site.ID"
              [disabled]="site.isApproved !== 'Approved' || site.domain === 'default' || site.siteUrl === 'default'">
              {{ site.domain || ('Default site #' + site.ID) }}
            </option>
          </select>

          <p *ngIf="hasDefaultSite" class="text-xs text-gray-500 mt-1">
            Default/null site is enforced.
          </p>

          <div *ngIf="getControl('siteID')?.touched && getControl('siteID')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('siteID')?.hasError('required')">
              Site is required.
            </span>
          </div>
        </div>

        <!-- Social Media ID -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            🆔 Page ID <small class="text-red-300">(required)</small>
          </label>
          <textarea
            formControlName="socialMediaID"
            rows="1"
            (keydown.enter)="$event.preventDefault()"
            placeholder="Enter Page ID..."
            class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
          </textarea>

          <div *ngIf="getControl('socialMediaID')?.touched && getControl('socialMediaID')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('socialMediaID')?.hasError('required')">
              Page ID is required.
            </span>
          </div>
        </div>

        <!-- Access Token -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            🔑 Access Token <small class="text-red-300">(required)</small>
          </label>
          <textarea
            formControlName="accessToken"
            rows="1"
            (keydown.enter)="$event.preventDefault()"
            placeholder="Enter access token..."
            class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm shadow-sm transition-colors">
          </textarea>

          <div *ngIf="getControl('accessToken')?.touched && getControl('accessToken')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('accessToken')?.hasError('required')">
              Access Token is required.
            </span>
            <span *ngIf="getControl('accessToken')?.hasError('minlength')">
              Access Token must be at least 190 characters.
            </span>
          </div>
        </div>

        <!-- App ID -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            📎 App ID <small class="text-red-300">(required)</small>
          </label>
          <textarea
            formControlName="appID"
            rows="1"
            (keydown.enter)="$event.preventDefault()"
            placeholder="Enter App ID..."
            class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm shadow-sm transition-colors">
          </textarea>

          <div *ngIf="getControl('appID')?.touched && getControl('appID')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('appID')?.hasError('required')">
              App ID is required.
            </span>
            <span *ngIf="getControl('appID')?.hasError('minlength')">
              App ID is required.
            </span>
          </div>
        </div>

        <!-- App Secret -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            🔐 App Secret <small class="text-red-300">(required)</small>
          </label>
          <textarea
            formControlName="appSecret"
            rows="1"
            (keydown.enter)="$event.preventDefault()"
            placeholder="Enter App Secret..."
            class="w-full resize-none border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                   focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm shadow-sm transition-colors">
          </textarea>

          <div *ngIf="getControl('appSecret')?.touched && getControl('appSecret')?.invalid"
               class="mt-1 text-xs text-red-500">
            <span *ngIf="getControl('appSecret')?.hasError('required')">
              App Secret is required.
            </span>
            <span *ngIf="getControl('appSecret')?.hasError('minlength')">
              App Secret is required.
            </span>
          </div>
        </div>

      </div>
    </div>

    <!-- Action Buttons (same style as edit-site / edit-user) -->
    <div class="mt-10 pt-6 border-t border-gray-200 flex justify-end space-x-4">
      <!-- Cancel Button -->
      <button
        type="button"
        (click)="onCancel()"
        [disabled]="isSubmitting"
        class="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 
               hover:bg-gray-50 hover:border-gray-400 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
        Cancel
      </button>

      <!-- Submit Button -->
      <button
        type="submit"
        [disabled]="platformForm.invalid || isSubmitting"
        class="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold 
               hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">

        <!-- Loading Spinner -->
        <span *ngIf="isSubmitting" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
               xmlns="http://www.w3.org/2000/svg"
               fill="none"
               viewBox="0 0 24 24">
            <circle class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4">
            </circle>
            <path class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          Saving...
        </span>

        <!-- Normal State -->
        <span *ngIf="!isSubmitting">
          Save Changes
        </span>
      </button>
    </div>

  </form>
</div>

<app-footer-grid *ngIf="!embeddedMode"></app-footer-grid>
